import { datadogLogs } from '@datadog/browser-logs';
import { Box, Typography, CircularProgress, Snackbar } from '@mui/material';
import { ContentTileComponent } from '../../Components/ContentTitle';
import LayersComponent from '../../Components/Layers';
import { MapComponent } from '../../Components/Map';
import {
  buildSources,
  buildLayersPatterns,
  buildLayersFill,
  buildLayersOutline,
  buildInspectorTool,
  buildLayersImagery,
  buildLayersLine,
  buildLayersCircle,
  ResultSource,
} from '../../core/builders/layer.builder';
import { IResult } from '../../core/interfaces/result.interface';
import { Res } from '../../resources';
import { useEffect, useRef, useState } from 'react';
import * as turf from '@turf/turf';
import { Map as MapBox } from 'mapbox-gl';
import { popup } from '../SurveyResults';
import { IProject } from '../../core/interfaces/project.interface';
import { MAP_STYLES } from '../../Components/Map/controls/MapStyleSwitchControl';
import { LAYER_TYPES } from '../../Utils/layer';
import useGentian from '../../core/hooks/useGentian';

export interface ResultPreviewProps {
  result: IResult;
  project?: IProject;
  isSaving: boolean;
  isPublishing: boolean;
  onMapLoad?: (map: MapBox) => void;
  onResultUpdate?: (updatedResult: IResult) => void;
}

export const ResultPreview: React.FC<ResultPreviewProps> = ({
  result,
  isSaving,
  isPublishing,
  project,
  onMapLoad,
  onResultUpdate,
}) => {
  const { generatePdfDownloadUrl } = useGentian();
  const [selectedCategories, setSelectedCategories] = useState<{ id: string; visible: boolean }[]>([]);
  const [mapLayers, setMapLayers] = useState<mapboxgl.AnyLayer[]>([]);
  const [mapSources, setMapSources] = useState<ResultSource[]>([]);
  const [mapPatterns, setMapPatterns] = useState<Record<string, string>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const inspector = useRef(new Map<string, string>());

  const handleDownloadButton = async () => {
    try {
      // Validate we have required data
      if (!result || !project) {
        throw new Error('Missing project or result data');
      }

      // Extract project ID (the 681495f61fa4cb6e1752e696 part)
      const projectId = project._id;
      if (!projectId) {
        throw new Error('Project ID not found');
      }

      // Format the date from result.createdAt (assuming it's a Date object)
      const formattedDate = result.createdAt
        ? new Date(result.createdAt).toISOString().split('T')[0].replace(/-/g, '-')
        : 'unknown-date';

      const formattedProjectName = project.name
        .replace(/\s+/g, '-') // Replace spaces with underscores
        .replace(/[^a-zA-Z0-9_.-]/g, '');

      // Construct the S3 key dynamically
      const s3Key = `GENTIAN/Delivery/2024-09_Hesti/${formattedDate}_${projectId}/${formattedProjectName}_${formattedDate}_TrueSight_Gentian.pdf`;

      // Get the pre-signed URL from your NestJS backend
      const { url } = await generatePdfDownloadUrl.mutateAsync(s3Key, {
        onError: (error) => {
          datadogLogs.logger.error('Download failed', {
            error: error,
            s3Key,
            time: new Date().toISOString(),
          });
        },
      });

      // Fetch the PDF content
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch the PDF');
      }
      const blob = await response.blob();

      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${formattedProjectName}_${formattedDate}_TrueSight_Gentian.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up after a small delay
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }, 100);

      setSnackbarMessage('Report downloaded successfully');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Download error:', error);
      setSnackbarMessage('Failed to download the file. Please try again later.');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (
      !!result?.layers?.length &&
      (!!result?.layers[0].details.classes.length || !!result?.layers?.find((l) => !!l.imageryUri))
    ) {
      const sources =
        result?.layers
          ?.map((l) => buildSources(l, result.shapes))
          .flat()
          .reverse() || [];
      const sourcesFill = sources.filter((source) => source.layerType === LAYER_TYPES.Fill);
      const sourcesLine = sources.filter((source) => source.layerType === LAYER_TYPES.Line);
      const sourcesCircle = sources.filter((source) => source.layerType === LAYER_TYPES.Circle);

      // Order of layers is important: last ones are displayed on top of the previous ones
      const layers = [
        ...(buildLayersImagery(sources) || []),
        ...(buildLayersFill(sourcesFill, result) || []),
        ...(buildLayersOutline(sourcesFill, result) || []),
        ...(buildLayersLine(sourcesLine, result) || []),
        ...(buildLayersCircle(sourcesCircle, result) || []),
      ].filter((l) => l?.id);

      const patterns = buildLayersPatterns(sources, result);

      setMapPatterns(patterns);
      setMapSources([...sources]);
      setMapLayers([...layers]);
    }
  }, [result]);

  useEffect(() => {
    if (result?.layers?.length && mapSources.length) {
      inspector.current = new Map(buildInspectorTool(mapSources, result));
    }
  }, [mapSources]);

  const handleOnMouseEnter = (
    map: mapboxgl.Map,
    sourceId: string,
    e: mapboxgl.MapMouseEvent & mapboxgl.EventData,
  ) => {
    popup.setLngLat(e.lngLat).setHTML(inspector.current.get(sourceId)).addTo(map);
  };

  useEffect(() => {
    if (mapLayers?.length) {
      const allClasses = result?.layers
        .map((layer) =>
          layer.details.classes.map((c) => ({
            id: `${layer.resultId}_${layer.details.columnName}_${c.category.toString().replace(/\s/g, '-')}`,
            visible: layer.details.defaultEnabled !== false && c.defaultEnabled !== false, // Default to true if undefined
          })),
        )
        .flat();
      const selCategories = mapLayers?.map((mapLayer) => ({
        id: mapLayer.id,
        visible: allClasses.find((c) => (mapLayer.id as string).startsWith(c.id))?.visible !== false || false,
      }));
      setSelectedCategories([...selCategories]);
    }
  }, [mapLayers]);

  return (
    <Box
      sx={{
        width: 'calc(100vw - 320px)',
        backgroundColor: 'white',
      }}
    >
      <ContentTileComponent title="Results Preview" />
      {isSaving || isPublishing ? (
        <Box
          sx={{
            width: '100%',
            height: 'calc(100vh - var(--header-height) - 80px - var(--footer-height))',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body1">{isSaving ? 'Saving...' : 'Publishing...'}</Typography>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            height: 'calc(100vh - var(--header-height) - 80px - var(--footer-height))',
          }}
        >
          <Box
            sx={{
              overflow: 'auto',
              height: 'calc(100vh - var(--header-height) - 80px - var(--footer-height))',
            }}
          >
            <LayersComponent
              result={result}
              project={project}
              productType={project?.products[0]?.name}
              onDownloadButton={handleDownloadButton}
              onCheckboxChange={(checked, data) => {
                // Parse the class ID to extract layer and class information
                const [layerResultId, columnName, ...categoryParts] = data.id.split('_');
                const category = categoryParts.join('_').replace(/-/g, ' ');

                const updatedResult = {
                  ...result,
                  layers: result.layers?.map((layer) => {
                    if (layer.resultId === layerResultId) {
                      return {
                        ...layer,
                        details: {
                          ...layer.details,
                          classes: layer.details.classes?.map((classItem) => {
                            if (classItem.category === category) {
                              return {
                                ...classItem,
                                defaultEnabled: checked,
                              };
                            }
                            return classItem;
                          }),
                        },
                      };
                    }
                    return layer;
                  }),
                };

                onResultUpdate?.(updatedResult);

                const selectedCategory = selectedCategories.filter(
                  (c) => c.id === data.id || c.id.startsWith(`${data.id}_`),
                );
                if (selectedCategory?.length) {
                  selectedCategory.forEach((c) => (c.visible = checked));
                } else {
                  selectedCategories.push({ id: data.id, visible: checked });
                }
                setSelectedCategories([...selectedCategories]);
              }}
              onSelect={(selected, layer) => {
                const updatedResult = {
                  ...result,
                  layers: result.layers?.map((l) =>
                    l.resultId === layer.resultId
                      ? {
                          ...l,
                          details: {
                            ...l.details,
                            defaultEnabled: selected,
                            classes: l.details.classes?.map((classItem) => ({
                              ...classItem,
                              defaultEnabled: selected,
                            })),
                          },
                        }
                      : l,
                  ),
                };

                onResultUpdate?.(updatedResult);

                layer.details.classes?.forEach((classItem) => {
                  const classId = `${layer.resultId}_${layer.details.columnName}_${classItem.category.toString().replace(/\s/g, '-')}`;
                  const categoryIndex = selectedCategories.findIndex((c) => c.id === classId);
                  if (categoryIndex !== -1) {
                    selectedCategories[categoryIndex].visible = selected;
                  }
                });
                setSelectedCategories([...selectedCategories]);
              }}
            />
          </Box>

          {!!result?.layers?.length &&
            (!!result?.layers[0].details.classes.length || !!result?.layers?.find((l) => !!l.imageryUri)) && (
              <MapComponent
                accessToken={Res.components.maps.MAPBOX_TOKEN || ''}
                readOnly
                style={MAP_STYLES.SATELLITE}
                hideMapStyleSwitchControl
                sources={mapSources}
                patterns={mapPatterns}
                layers={mapLayers}
                onLoad={(map) => {
                  const sources =
                    result?.layers
                      ?.map((l) => buildSources(l, result.shapes))
                      .flat()
                      .reverse() || [];
                  const sourcesFill = sources.filter((source) => source.layerType === LAYER_TYPES.Fill);
                  const sourcesLine = sources.filter((source) => source.layerType === LAYER_TYPES.Line);
                  const sourcesCircle = sources.filter((source) => source.layerType === LAYER_TYPES.Circle);

                  const layers = [
                    ...(buildLayersImagery(sources) || []),
                    ...(buildLayersFill(sourcesFill, result) || []),
                    ...(buildLayersOutline(sourcesFill, result) || []),
                    ...(buildLayersLine(sourcesLine, result) || []),
                    ...(buildLayersCircle(sourcesCircle, result) || []),
                  ].filter((l) => l?.id);

                  const patterns = buildLayersPatterns(sources, result);

                  setMapPatterns(patterns);
                  setMapSources([...sources]);
                  setMapLayers([...layers]);

                  sources?.forEach((source) => {
                    if (map.getSource(source.id)) {
                      map.removeSource(source.id);
                    }
                    map.addSource(source.id, source.data);

                    map.on('mousedown', source.id, (e) => {
                      handleOnMouseEnter(map, source.id, e);
                    });

                    map.on('mouseenter', source.id, () => {
                      map.getCanvas().style.cursor = 'pointer';
                    });

                    map.on('mouseleave', source.id, () => {
                      map.getCanvas().style.cursor = '';
                    });
                  });

                  Object.entries(patterns).forEach(([id, pattern]) => {
                    map.loadImage(pattern, (error, image) => {
                      if (error) {
                        datadogLogs.logger.error('Error loading image', {
                          error,
                          name: 'loadImage',
                          id,
                          pattern,
                        });
                        throw error;
                      }
                      if (map.hasImage(id)) {
                        map.removeImage(id);
                      }
                      map.addImage(id, image);
                    });
                  });

                  layers?.forEach((layer) => {
                    if (map.getLayer(layer.id)) {
                      map.removeLayer(layer.id);
                    }
                    map.addLayer(layer);
                  });

                  if (sources.length) {
                    const geoJsonSource = sources.find(
                      (s) => s.data.type === 'geojson' && 'data' in s.data && s.data.data,
                    );
                    const boundSource =
                      geoJsonSource?.data.type === 'geojson' && 'data' in geoJsonSource.data
                        ? geoJsonSource.data.data
                        : project?.AOI;
                    if (boundSource) {
                      map.fitBounds(turf.bbox(boundSource) as any, {
                        padding: 20,
                        maxZoom: 14,
                      });
                    }
                  }

                  onMapLoad(map);
                }}
                filteredLayers={selectedCategories}
                sx={{
                  width: 'calc(100vw - 660px)',
                  height: 'calc(100vh - var(--header-height) - var(--footer-height) - 80px)',
                }}
              />
            )}
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};
